import { QueryClient } from '@tanstack/react-query';
import { isEqual } from 'lodash';
import { Map } from 'immutable';
import { Dispatch } from 'redux';
import Label, { LabellableType } from '@labels/models/label';
import { applyLabelEdit, applyLabelRemoval, updateLabelsWithOrder } from '@labels/utils';

import { WorkspaceQueryInvalidator } from '@workspaces/queries/queryInvalidator';
import { ProjectMembersQueryInvalidator } from '@projectMembers/queries/queryInvalidator';
import { CURRENT_USER_KEY } from '../modules/authentication/queries/keys';
import User, { UserJson } from '../models/user/user';
import { QueryFilters, QueryKeys } from '../modules/projectView/queries/queryKeys';
import {
	addItem,
	addOrUpdateItemPaginated,
	QueryDataUpdater,
	removeItems,
	removeItemsPaginated,
	updateAllItems,
	updateAllItemsPaginated,
	updateItemProperty,
	updateItems,
	updateItemsPaginated,
	updateItemsPropertyPaginated,
	updateQueriesData,
	type PaginatedDataUpdater
} from '../queries/updaters';
import { AssetJSON } from '../modules/projectView/models/asset';
import { AssetRoundJSON } from '../modules/projectView/models/assetRound';
import { queryClient as globalQueryClient } from '../../store/store';
import Asset from '../../entities/asset';
import Folder from '../../entities/folder';
import { FolderJSON } from '../modules/projectView/models/folder';
import { AssetDetailQueryInvalidator } from '../modules/assetView/queries/queryInvalidator';
import { AssetDetailQueryDataUpdater } from '../modules/assetView/queries/queryDataUpdater';
import { QueryKeys as AssetDetailQueryKeys } from '../modules/assetView/queries/keysAndFilters';
import { AssetsQueryInvalidator } from '../modules/projectView/queries/queryInvalidator';
import { QueryType } from '../queries/types';

export type currentUserJson = {
	userID: number,
	userPreferencesID: number,
	notificationsPreferencesID: number
};

type Reducers = {
	currentUser?: currentUserJson,
	users?: Map<number, UserJson>,
	userPreferences?: Map<string, unknown>,
	projectsViewOptions?: Map<string, unknown>,
};

export type ReduxStore = {
	getState: () => Reducers,
	dispatch: Dispatch,
	subscribe: ( callback: () => void ) => () => void
};

type QueryLocation = {
	projectID: number,
	folderID?: number | null
}

type InvalidationParams = QueryLocation & { refetchActiveQueries?: boolean }
type ItemsType = 'assets' | 'folders';
type ParentUploadsData = Array<{ uploadedFolderID: number, uploadedAtFolderID: number | null }>;

const idsOf = ( items: Asset[] | Folder[] ) => items.map( i => i.id as number );
const asItemsWithId = ( ids: number[] ) => ids.map( id => ( { id } ) );

/*
* This class is responsible for updating the query client when the redux store changes.
*/

export default class QueryClientUpdater {
	store: ReduxStore;
	queryClient: QueryClient;
	reducers: Reducers;

	constructor( { store, queryClient } : { store: ReduxStore, queryClient: QueryClient } ) {
		this.store = store;
		this.queryClient = queryClient;
		this.reducers = {};
	}

	start() {
		this.observeReducer( 'currentUser', this.updateCurrentUser );
		this.observeReducer( 'users', this.updateCurrentUser );
		this.observeReducer( 'userPreferences', this.updateUserPreferences );
		this.observeReducer( 'projectsViewOptions', this.updateProjectViewOptions );
	}

	static createdFolder( folderJson: FolderJSON ) {
		const projectID = folderJson.project_id;
		const folderID = folderJson.organizer_type === 'Folder' ? folderJson.organizer_id : null;
		this.updateFolders( { projectID, folderID, updater: addItem( folderJson ) } );
		this.invalidateFolders( { projectID, folderID } );
		this.invalidateFoldersTree( { projectID } );
	}

	static updatedLabelsOrder( {
		labellableID, labellableType, orderedLabelIDs
	}: { labellableID: number, labellableType: LabellableType, orderedLabelIDs: number[] } ) {
		if ( labellableType === 'Project' ) {
			this.updateAssets( {
				projectID: labellableID,
				updater: updateAllItemsPaginated(
					asset => ( {
						...asset,
						labels: updateLabelsWithOrder( {
							labels: asset.labels,
							orderedLabelIds: orderedLabelIDs
						} )
					} )
				)
			} );

			this.updateFolders( {
				projectID: labellableID,
				updater: updateAllItems(
					folder => ( {
						...folder,
						labels: updateLabelsWithOrder( {
							labels: folder.labels || [],
							orderedLabelIds: orderedLabelIDs
						} )
					} )
				)
			} )

			this.invalidateAssetsAndFolders( {
				projectID: labellableID,
				refetchActiveQueries: false
			} );

			AssetDetailQueryInvalidator.invalidateAllAssetDetail( { refetchActiveQueries: true } );
		}
	}

	static labelEdited( { labelJson }: { labelJson: ReturnType<Label[ 'toJSON' ]> } ) {
		if ( labelJson.labellable_type === 'Project' ) {
			this.updateAssets( {
				projectID: labelJson.labellable_id,
				updater: updateAllItemsPaginated(
					asset => ( { ...asset, labels: applyLabelEdit( asset.labels, labelJson ) } )
				)
			} );

			this.updateFolders( {
				projectID: labelJson.labellable_id,
				updater: updateAllItems(
					folder => ( { ...folder, labels: applyLabelEdit( folder.labels || [], labelJson ) } )
				)
			} );

			AssetDetailQueryDataUpdater.forEditedLabel( labelJson.id, labelJson );

			this.invalidateAssetsAndFolders( {
				projectID: labelJson.labellable_id,
				refetchActiveQueries: false
			} );

			AssetsQueryInvalidator.invalidateProjectOverviews( labelJson.labellable_id );
		}
	}

	static updatedOrDeletedLabel( { labelJson }: { labelJson: ReturnType<Label[ 'toJSON' ]> } ) {
		if ( labelJson.labellable_type === 'Project' ) {
			this.updateAssets( {
				projectID: labelJson.labellable_id,
				updater: updateAllItemsPaginated(
					asset => ( { ...asset, labels: applyLabelRemoval( asset.labels, labelJson.id ) } )
				)
			} );

			this.updateFolders( {
				projectID: labelJson.labellable_id,
				updater: updateAllItems(
					folder => ( { ...folder, labels: applyLabelRemoval( folder.labels || [], labelJson.id ) } )
				)
			} );

			AssetDetailQueryDataUpdater.forDeletedLabel( labelJson.id );

			this.invalidateAssetsAndFolders( {
				projectID: labelJson.labellable_id,
				refetchActiveQueries: false
			} );

			AssetsQueryInvalidator.invalidateProjectOverviews( labelJson.labellable_id );
		}
	}

	static createdOrUpdatedAsset( {
		assetJson, projectID, parentUploadsData
	}: {
		assetJson: AssetJSON, projectID: number, parentUploadsData: ParentUploadsData
	} ) {
		const { folder_id: folderID } = assetJson;

		this.updateAssets( {
			projectID,
			folderID,
			updater: addOrUpdateItemPaginated<AssetJSON>( assetJson )
		} );
		this.invalidateItems( { projectID } );
		AssetDetailQueryInvalidator.invalidateAssetDetail( assetJson.id );
		AssetsQueryInvalidator.invalidateProjectOverviews( projectID );

		globalQueryClient.invalidateQueries( {
			queryKey: QueryKeys.forMaxRoundNumber( projectID ),
			refetchType: 'active'
		} );

		/* The parentUploadsData parameter provides information about the folder uploads
		under which the asset creation happened (in case it was indeed created through a
		folder upload). The first element of the array describes the upload of the folder
		directly containing the asset; the second one describes the parent upload of the
		first one, and so on and so forth. The last element is the root folder being uploaded.
		Each folder upload description comes with the ID of the folder being uploaded, and
		the ID of the folder containing that folder.

		This information is used here to synchronously update the folders affected by this
		asset creation –updating asset count and cover if needed–, as an alternative to just
		invalidating and refetching folder queries, which would be innefficient if many assets
		are being uploaded at once.
		*/
		const { isDocument } = Asset.fromJSON( assetJson );

		parentUploadsData.forEach( ( { uploadedFolderID, uploadedAtFolderID } ) => {
			const updateFolderWithNewAsset = ( folder: FolderJSON ) => {
				const countToUpdate = isDocument ? 'documents_count' : 'images_count';
				const shouldSetNewAssetAsCover = folder.id === folderID && !folder.cover_url;
				const coverUrl = shouldSetNewAssetAsCover
					? assetJson.current_version.thumbnail_url
					: folder.cover_url;

				return {
					...folder,
					[ countToUpdate ]: folder[ countToUpdate ] + 1,
					cover_url: coverUrl
				}
			}

			this.updateFolders( {
				projectID,
				folderID: uploadedAtFolderID,
				updater: updateItems( [ uploadedFolderID ], updateFolderWithNewAsset )
			} );
		} );
	}

	static createdAssetRound( {
		newRoundJson, assetID, projectID
	}: {
		newRoundJson: AssetRoundJSON,
		assetID: number,
		projectID: number
	} ) {
		const lastActivityAt = Date();

		this.updateAssets( {
			projectID,
			updater: updateItemsPaginated<AssetJSON>(
				[ assetID ],
				asset => ( {
					...asset,
					name: newRoundJson.name,
					current_version: newRoundJson,
					version_id: newRoundJson.id,
					asset_versions_count: asset.asset_versions_count + 1,
					last_activity_at: lastActivityAt,
					updated_at: lastActivityAt,
					has_comments_in_current_round: false
				} )
			)
		} );
		this.invalidateAssets( { projectID } );

		AssetDetailQueryInvalidator.invalidateAssetDetail( assetID );
		AssetDetailQueryDataUpdater.forCreatedAssetRound( assetID, newRoundJson );

		globalQueryClient.invalidateQueries( {
			queryKey: QueryKeys.forMaxRoundNumber( projectID ),
			refetchType: 'active'
		} );
	}

	static updatedAssetCurrentVersion( {
		currentVersionJson, assetID, projectID, unpreviewable, metadataRating
	}: {
		currentVersionJson: AssetRoundJSON,
		assetID: number,
		projectID: number,
		folderID?: number | null,
		unpreviewable?: boolean,
		metadataRating?: number | null
	} ) {
		this.updateAssets( {
			projectID,
			updater: updateItemsPaginated(
				[ assetID ],
				( asset ) => {
					if ( asset.current_version.id !== currentVersionJson.id ) {
						return asset;
					}

					return {
						...asset,
						name: currentVersionJson.name,
						current_version: currentVersionJson,
						...( unpreviewable !== undefined && { unpreviewable } ),
						...( metadataRating !== undefined && { metadata_rating: metadataRating } )
					};
				}
			)
		} );
		this.invalidateAssets( { projectID, refetchActiveQueries: true } );

		AssetDetailQueryDataUpdater.forUpdatedCurrentRound(
			assetID, currentVersionJson, unpreviewable, { metadataRating }
		);
		AssetDetailQueryInvalidator.invalidateAssetDetail( assetID, { refetchActiveQueries: true } );
	}

	static deletedAssetsLastRound( { assets }: { assets: Asset[] } ) {
		this.extractingQueryLocation( { assets }, ( { projectID } ) => {
			this.invalidateAssets( { projectID, refetchActiveQueries: true } );

			globalQueryClient.invalidateQueries( {
				queryKey: QueryKeys.forMaxRoundNumber( projectID ),
				refetchType: 'active'
			} );
		} );
	}

	static changedAssetsApprovalStatus( { assets, toApproved }: { assets: Asset[], toApproved: boolean } ) {
		const approvedAt = toApproved ? new Date().toISOString() : null;

		this.extractingQueryLocation( { assets }, ( { projectID } ) => {
			this.updateAssets( {
				projectID,
				updater: updateItemsPropertyPaginated(
					idsOf( assets ),
					'approved_at',
					approvedAt
				)
			} );
			this.invalidateItems( { projectID } );
		} );

		assets.forEach( ( asset ) => {
			AssetDetailQueryDataUpdater.forApprovalStatusChange( asset.id, approvedAt );
			AssetDetailQueryInvalidator.invalidateAssetDetail( asset.id, { refetchActiveQueries: true } );
		} );
	}

	static assetsWereDeleted( { assetIDs, projectID }: { assetIDs: number[], projectID: number } ) {
		this.updateAssets( {
			projectID,
			updater: removeItemsPaginated( asItemsWithId( assetIDs ) )
		} );
		this.invalidateAssetsAndFolders( { projectID } );

		globalQueryClient.invalidateQueries( {
			queryKey: QueryKeys.forMaxRoundNumber( projectID ),
			refetchType: 'active'
		} );
	}

	static itemsWereOptimisticallyDeleted(
		{ projectID, assetIDs, folderIDs = [] }:
		{ projectID: number, assetIDs: number[], folderIDs?: number[] }
	) {
		if ( assetIDs.length > 0 ) {
			this.updateAssets( { projectID, updater: removeItemsPaginated( asItemsWithId( assetIDs ) ) } );
		}
		if ( folderIDs.length > 0 ) {
			this.updateFolders( { projectID, updater: removeItems( asItemsWithId( folderIDs ) ) } );
		}
	}

	static itemsDeletionWasUndone( { projectID }: { projectID: number } ) {
		this.invalidateFoldersTree( { projectID } );

		globalQueryClient.invalidateQueries( {
			queryKey: QueryKeys.forMaxRoundNumber( projectID ),
			refetchType: 'active'
		} );

		globalQueryClient.invalidateQueries( {
			...QueryFilters.forItemsCount( { projectID } ),
			refetchType: 'active'
		} );

		return globalQueryClient.invalidateQueries( {
			...QueryFilters.forAssetAndFolderLists( { projectID } ),
			refetchType: 'active'
		} );
	}

	static lastRoundWasOptimisticallyDeleted(
		{ projectID, assetID, previousRound }:
		{ projectID: number, assetID: number, previousRound: AssetRoundJSON }
	) {
		this.updateAssets( {
			projectID,
			updater: updateItemsPaginated<AssetJSON>( [ assetID ], asset => ( {
				...asset,
				name: previousRound.name,
				current_version: previousRound,
				version_id: previousRound.id,
				asset_versions_count: Math.max( 0, asset.asset_versions_count - 1 )
			} ) )
		} );
	}

	static lastRoundDeletionWasUndone(
		{ projectID, assetID, deletedRound }:
		{ projectID: number, assetID: number, deletedRound: AssetRoundJSON }
	) {
		this.updateAssets( {
			projectID,
			updater: updateItemsPaginated<AssetJSON>( [ assetID ], asset => ( {
				...asset,
				name: deletedRound.name,
				current_version: deletedRound,
				version_id: deletedRound.id,
				asset_versions_count: asset.asset_versions_count + 1
			} ) )
		} );
	}

	static lastRoundDeletionWasCommitted( { assetID }: { assetID: number } ) {
		AssetDetailQueryInvalidator.invalidateAssetDetail( assetID, { refetchActiveQueries: true } );

		globalQueryClient.invalidateQueries( {
			queryKey: AssetDetailQueryKeys.forAssetRounds( assetID ),
			refetchType: 'active'
		} );
	}

	static foldersWereDeleted( { folderIDs, projectID }: { folderIDs: number[], projectID: number } ) {
		this.updateFolders( {
			projectID,
			updater: removeItems( asItemsWithId( folderIDs ) )
		} );
		this.invalidateFolders( { projectID } );
		this.invalidateFoldersTree( { projectID } );
	}

	static folderNewActivityWasSeen( { folder }: { folder: Folder } ) {
		const { id, projectID, organizerID, organizerType } = folder;
		const folderID = organizerType === 'Folder' ? organizerID : null;

		this.updateFolders( {
			projectID,
			folderID,
			updater: updateItemProperty( id, 'has_new_activity', false ),
			queryType: 'all'
		} );

		this.invalidateFolders( { projectID } );
	}

	static itemsWereUnfolded( { assets, folders }: { assets: Asset[], folders: Folder[] } ) {
		this.extractingQueryLocation( { assets, folders }, ( { projectID, folderID } ) => {
			this.updateAssets( { projectID, folderID, updater: removeItemsPaginated( assets ) } );
			this.updateFolders( { projectID, folderID, updater: removeItems( folders ) } );

			this.invalidateAssets( { projectID, folderID } );
			this.invalidateAssets( { projectID, folderID: null } );
			this.invalidateFolders( { projectID } );

			if ( folders.length > 0 ) {
				this.invalidateFoldersTree( { projectID } );
			}
		} );
	}

	static folderCoverChanged( folder: Folder ) {
		const { projectID, folderID } = folder;
		this.invalidateFolders( { projectID, folderID } )
	}

	static folderRenamed(
		{ folderID, projectID, newName }: { folderID: number, projectID: number, newName: string }
	) {
		this.updateFolders( {
			projectID,
			updater: updateItemProperty( folderID, 'name', newName )
		} );

		this.invalidateFolders( { projectID } );
		this.invalidateFoldersTree( { projectID } );
	}

	static commentCreated( { forAsset }: { forAsset: Asset } ) {
		this.invalidateAssetsAndFoldersForCommentInAsset( forAsset );
	}

	static commentCompletionToggled( { forAsset }: { forAsset: Asset } ) {
		this.invalidateAssetsAndFoldersForCommentInAsset( forAsset );
	}

	static commentDeleted( { forAsset }: { forAsset: Asset } ) {
		this.invalidateAssetsAndFoldersForCommentInAsset( forAsset );
	}

	static forFetchedAssetRounds( roundsJSON: AssetRoundJSON[] ) {
		const assetIDs = roundsJSON.map( round => round.asset_id );

		AssetDetailQueryDataUpdater.forFetchedRounds( roundsJSON );
		AssetDetailQueryInvalidator.invalidateAssetsDetail( assetIDs );
	}

	static forInvitedWorkspaceMembers( { workspaceID }: { workspaceID: number } ) {
		WorkspaceQueryInvalidator.invalidateWorkspaceMembers( workspaceID );
	}

	static forDeletedWorkspaceMember( { workspaceID }: { workspaceID: number } ) {
		WorkspaceQueryInvalidator.invalidateWorkspaceMembers( workspaceID );
		ProjectMembersQueryInvalidator.invalidateProjectMembers();
	}

	private static invalidateAssetsAndFoldersForCommentInAsset( asset: Asset ) {
		const { projectID } = asset;
		this.invalidateItems( { projectID } );
	}

	private updateCurrentUser = ( _: unknown, { currentUser, users }: Reducers ) => {
		this.queryClient.setQueryData(
			CURRENT_USER_KEY,
			( oldUserJson?: UserJson ) => {
				if ( !oldUserJson || !currentUser || !users ) return undefined;
				const newUserJson = users.get( currentUser.userID );
				return { ...oldUserJson, ...newUserJson };
			}
		);
	}

	private updateProjectViewOptions = ( projectsViewOptionsReducer: Reducers['projectsViewOptions'] ) => {
		this.queryClient.setQueryData(
			CURRENT_USER_KEY,
			( oldUserJson? : UserJson ) => {
				if ( !oldUserJson ) return undefined;
				const user = User.fromJson( oldUserJson );
				user.updateProjectsOrder( projectsViewOptionsReducer?.get( 'customSort' ) as number[] );
				return user.toJson();
			}
		);
	}

	private updateUserPreferences = ( userPreferencesReducer: Reducers['userPreferences'] ) => {
		this.queryClient.setQueryData(
			CURRENT_USER_KEY,
			( oldUserJson? : UserJson ) => {
				if ( !oldUserJson ) return undefined;
				return {
					...oldUserJson,
					user_preference: userPreferencesReducer?.first() as UserJson['user_preference']
				};
			}
		);
	}

	private observeReducer<ReducerName extends keyof Reducers>(
		reducerName: ReducerName, callback: ( reducer: Reducers[ReducerName], state: Reducers ) => void
	) {
		return this.store.subscribe( () => {
			const allReducers = this.store.getState();
			const nextState = allReducers[ reducerName ];
			const previousState = this.reducers[ reducerName ];
			if ( isEqual( nextState, previousState ) ) return;
			this.reducers[ reducerName ] = nextState;
			callback( nextState, allReducers );
		} );
	}

	// --- Helper methods for updating and invalidating asset and folder queries ---
	private static updateAssets(
		params: QueryLocation & { updater: PaginatedDataUpdater<AssetJSON>, queryType?: QueryType }
	) {
		this.updateItems( { ...params, itemsType: 'assets' } );
	}

	private static updateFolders(
		params: QueryLocation & { updater: ( data: FolderJSON[] ) => FolderJSON[], queryType?: QueryType }
	) {
		this.updateItems( { ...params, itemsType: 'folders' } );
	}

	private static updateItems<T>( {
		projectID, folderID, itemsType, updater, queryType = 'active'
	}: QueryLocation & {
		itemsType: ItemsType,
		updater: QueryDataUpdater<T>,
		queryType?: QueryType
	} ) {
		updateQueriesData(
			globalQueryClient,
			QueryFilters.forAssetAndFolderLists( {
				projectID,
				folderID,
				only: itemsType,
				queryType
			} ),
			updater
		);
	}

	private static invalidateAssets( params: InvalidationParams ) {
		this.invalidateItems( { ...params, itemsType: 'assets' } );
	}

	private static invalidateFolders( params: InvalidationParams ) {
		this.invalidateItems( { ...params, itemsType: 'folders' } );
	}

	private static invalidateAssetsAndFolders( params: InvalidationParams ) {
		this.invalidateItems( params );
	}

	private static invalidateItems( {
		projectID, folderID, itemsType, refetchActiveQueries = false
	}: InvalidationParams & { itemsType?: ItemsType } ) {
		globalQueryClient.invalidateQueries( {
			...QueryFilters.forAssetAndFolderLists( { projectID, folderID, only: itemsType } ),
			refetchType: refetchActiveQueries ? 'active' : 'none'
		} );

		globalQueryClient.invalidateQueries( {
			...QueryFilters.forItemsCount( { projectID, folderID } ),
			refetchType: 'active'
		} );
	}

	private static invalidateFoldersTree( { projectID } : { projectID: number } ) {
		globalQueryClient.invalidateQueries( {
			queryKey: QueryKeys.forFolderTree( projectID ),
			refetchType: 'active'
		} );
	}

	private static extractingQueryLocation(
		{ assets = [], folders = [] }: { assets?: Asset[], folders?: Folder[] },
		callback: ( location: QueryLocation ) => void
	) {
		if ( assets.length === 0 && folders.length === 0 ) return;

		const { projectID, folderID } = assets[ 0 ] || folders[ 0 ];

		callback( { projectID, folderID } );
	}
}
